import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Building, Building2, Calculator, BarChart3, Check, LineChart } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import Button from "../../components/ui/Button";
import type { AccountingPlatform } from "../../types/auth";

interface Props {
  onComplete: () => Promise<void>;
}

interface PlatformOption {
  id: AccountingPlatform;
  label: string;
  Icon: typeof Calculator;
  oauth: boolean;
}

interface PendingTenant {
  tenantId: string;
  tenantName?: string;
  tenantType?: string;
}

const platforms: PlatformOption[] = [
  { id: "xero", label: "Xero", Icon: Calculator, oauth: true },
  { id: "quickbooks", label: "QuickBooks", Icon: LineChart, oauth: false },
  { id: "myob", label: "MYOB", Icon: BarChart3, oauth: false },
  { id: "sage", label: "Sage Intacct", Icon: Building, oauth: false },
];

const formatCallbackError = (reason: string | null): string | null => {
  if (!reason) return "We couldn't connect that account. Please try again.";
  if (reason === "access_denied") return "You declined access. Please try again to continue.";
  if (reason === "no_tenants") return "We couldn't find any organisations on that account.";
  return reason.startsWith("invalid") ? "That connection request expired. Please try again." : "We couldn't connect that account. Please try again.";
};

export default ({ onComplete }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pending, setPending] = useState<AccountingPlatform | "skip" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingTenants, setPendingTenants] = useState<PendingTenant[] | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [isSelectingTenant, setIsSelectingTenant] = useState(false);
  const [isCheckingPending, setIsCheckingPending] = useState(true);

  const accountingParam = searchParams.get("accounting");
  const reasonParam = searchParams.get("reason");

  const clearAccountingParams = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("accounting");
    next.delete("reason");
    next.delete("platform");
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (accountingParam === "error") {
      setError(formatCallbackError(reasonParam));
    }

    let cancelled = false;
    void (async () => {
      try {
        const response = await fetch("/api/onboarding/accounting/pending");
        if (!response.ok) return;
        const result = await response.json() as { pending?: { platform: "xero"; tenants: PendingTenant[] } | null };
        if (cancelled || !result.pending) return;
        setPendingTenants(result.pending.tenants);
        setSelectedTenantId(result.pending.tenants[0]?.tenantId ?? null);
      } finally {
        if (!cancelled) setIsCheckingPending(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startXero = async () => {
    setPending("xero");
    setError(null);

    const response = await fetch("/api/integrations/xero/start", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ returnTo: "/signup", flow: "onboarding" }),
    });

    const result = await response.json().catch(() => null) as { authUrl?: string; error?: string } | null;
    if (!response.ok || !result?.authUrl) {
      setError(result?.error ?? "Unable to start Xero sign-in.");
      setPending(null);
      return;
    }

    window.location.assign(result.authUrl);
  };

  const recordPlaceholderPlatform = async (platform: AccountingPlatform) => {
    setPending(platform);
    setError(null);

    const response = await fetch("/api/onboarding/accounting", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ platform, skipped: false }),
    });

    if (!response.ok) {
      const result = await response.json().catch(() => null) as { error?: string } | null;
      setError(result?.error ?? "Unable to save accounting selection.");
      setPending(null);
      return;
    }

    await onComplete();
  };

  const skipAccounting = async () => {
    setPending("skip");
    setError(null);

    const response = await fetch("/api/onboarding/accounting", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ platform: null, skipped: true }),
    });

    if (!response.ok) {
      const result = await response.json().catch(() => null) as { error?: string } | null;
      setError(result?.error ?? "Unable to save accounting selection.");
      setPending(null);
      return;
    }

    await onComplete();
  };

  const handlePlatformClick = (platform: AccountingPlatform) => {
    const config = platforms.find(item => item.id === platform);
    if (config?.oauth) {
      void startXero();
      return;
    }
    void recordPlaceholderPlatform(platform);
  };

  const submitTenant = async () => {
    if (!selectedTenantId) return;
    setIsSelectingTenant(true);
    setError(null);

    const response = await fetch("/api/onboarding/accounting/select-tenant", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tenantId: selectedTenantId }),
    });

    if (!response.ok) {
      const result = await response.json().catch(() => null) as { error?: string } | null;
      setError(result?.error ?? "Unable to save Xero connection.");
      setIsSelectingTenant(false);
      return;
    }

    setPendingTenants(null);
    clearAccountingParams();
    await onComplete();
  };

  const cancelTenantSelection = async () => {
    await fetch("/api/onboarding/accounting/pending", { method: "DELETE" }).catch(() => null);
    setPendingTenants(null);
    setSelectedTenantId(null);
    clearAccountingParams();
  };

  if (isCheckingPending) {
    return <div className="flex h-40 items-center justify-center text-sm font-medium text-slate-500">Checking accounting status...</div>;
  }

  if (pendingTenants && pendingTenants.length > 0) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
            <Building2 className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-slate-700">Choose a Xero organisation</p>
          <p className="text-xs text-slate-500">We found multiple organisations on this Xero account. Pick the one to connect first.</p>
        </div>

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
        )}

        <div className="space-y-2">
          {pendingTenants.map(tenant => {
            const isSelected = tenant.tenantId === selectedTenantId;
            return (
              <button
                key={tenant.tenantId}
                type="button"
                onClick={() => setSelectedTenantId(tenant.tenantId)}
                className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition ${isSelected ? "border-indigo-500 bg-indigo-50/40 ring-2 ring-indigo-100" : "border-slate-200 bg-white hover:border-slate-300"}`}
              >
                <div>
                  <p className="text-sm font-semibold text-slate-900">{tenant.tenantName ?? "Untitled organisation"}</p>
                  {tenant.tenantType && <p className="text-xs text-slate-500">{tenant.tenantType}</p>}
                </div>
                <span className={`flex h-5 w-5 items-center justify-center rounded-full border ${isSelected ? "border-indigo-500 bg-indigo-500 text-white" : "border-slate-300 bg-white"}`}>
                  {isSelected && <Check className="h-3 w-3" />}
                </span>
              </button>
            );
          })}
        </div>

        <div className="space-y-3">
          <Button type="button" className="h-12 w-full" onClick={() => void submitTenant()} disabled={!selectedTenantId || isSelectingTenant}>
            {isSelectingTenant ? "Connecting..." : "Connect this organisation"}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-12 w-full border-slate-300 bg-white text-sm font-semibold text-slate-700"
            onClick={() => void cancelTenantSelection()}
            disabled={isSelectingTenant}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to platforms
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
          <Building2 className="h-6 w-6" />
        </div>
        <p className="text-sm font-medium text-slate-700">Let&apos;s get connected to your accounting software</p>
        <p className="text-xs text-slate-500">Choose your accounting platform to unlock powerful financial insights.</p>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
      )}

      <div className="grid grid-cols-2 gap-3">
        {platforms.map(({ id, label, Icon, oauth }) => (
          <button
            key={id}
            type="button"
            onClick={() => handlePlatformClick(id)}
            disabled={pending !== null}
            className="flex flex-col items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-6 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-indigo-400 hover:bg-indigo-50/40 hover:text-indigo-700 disabled:opacity-60"
          >
            <Icon className="h-6 w-6 text-indigo-500" />
            <span>{label}</span>
            {pending === id && (
              <span className="text-xs font-normal text-slate-500">{oauth ? "Redirecting..." : "Saving..."}</span>
            )}
          </button>
        ))}
      </div>

      <div className="space-y-3 pt-2">
        <p className="text-center text-xs text-slate-500">You can always connect your accounting software later</p>
        <Button
          type="button"
          variant="outline"
          className="h-12 w-full border-slate-300 bg-white text-sm font-semibold text-slate-700"
          onClick={() => void skipAccounting()}
          disabled={pending !== null}
        >
          <ArrowRight className="h-4 w-4" />
          {pending === "skip" ? "Skipping..." : "Skip for now"}
        </Button>
      </div>
    </div>
  );
};
